import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, MapPin, Navigation } from 'lucide-react';
import { CHINA_PROVINCES } from './chinaCities.js';
import {
  buildLocationProfile,
  buildManualLocation,
  detectUserLocation,
  getGeolocationErrorMessage,
  setAutoLocationConsent
} from './geoUtils.js';

export const LocationConsentModal = ({ onAllow, onDeny, onManual, showToast }) => {
  const [detecting, setDetecting] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [province, setProvince] = useState(CHINA_PROVINCES[0]?.name || '');
  const [city, setCity] = useState(CHINA_PROVINCES[0]?.cities?.[0] || '');

  const cities = useMemo(
    () => CHINA_PROVINCES.find((p) => p.name === province)?.cities || [],
    [province]
  );

  useEffect(() => {
    if (cities.length && !cities.includes(city)) setCity(cities[0] || '');
  }, [province, cities, city]);

  const handleAllow = async () => {
    setDetecting(true);
    try {
      const detected = await detectUserLocation();
      const profile = buildLocationProfile({ ...detected, locationAgreed: true });
      showToast?.(`已定位到 ${profile.location}`);
      onAllow?.(profile);
    } catch (error) {
      showToast?.(getGeolocationErrorMessage(error), 'warning');
      setShowManual(true);
    } finally {
      setDetecting(false);
    }
  };

  const handleManualConfirm = () => {
    const manual = buildManualLocation(province, city);
    if (!manual) return showToast?.('请选择所在城市');
    onManual?.(buildLocationProfile(manual));
  };

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-zinc-900 border border-white/10 rounded-[2rem] w-full max-w-md p-8 shadow-2xl relative">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-amber-500/15 flex items-center justify-center mb-4">
            <MapPin size={32} className="text-amber-500" />
          </div>
          <h3 className="text-xl font-black text-white mb-2">是否允许获取您的位置？</h3>
          <p className="text-xs text-white/50 leading-relaxed max-w-sm">
            用于同城/附近领养推荐。我们仅识别您所在的城市，不会在主页公开精确坐标。
          </p>
        </div>

        {showManual ? (
          <div className="space-y-3 mb-6">
            <div className="flex gap-2">
              <select
                value={province}
                onChange={(e) => setProvince(e.target.value)}
                className="flex-1 bg-black/30 border border-white/10 rounded-lg px-2 py-2.5 text-xs text-white outline-none focus:border-amber-500/50"
              >
                {CHINA_PROVINCES.map((p) => (
                  <option key={p.name} value={p.name} className="bg-zinc-900">{p.name}</option>
                ))}
              </select>
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="flex-1 bg-black/30 border border-white/10 rounded-lg px-2 py-2.5 text-xs text-white outline-none focus:border-amber-500/50"
              >
                {cities.map((c) => (
                  <option key={c} value={c} className="bg-zinc-900">{c}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={handleManualConfirm}
              className="w-full py-3 rounded-xl font-black bg-amber-500 text-black"
            >
              确认城市并继续
            </button>
          </div>
        ) : (
          <div className="space-y-3 mb-2">
            <button
              type="button"
              onClick={handleAllow}
              disabled={detecting}
              className="w-full py-3.5 rounded-xl font-black bg-amber-500 text-black disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {detecting ? <Loader2 size={18} className="animate-spin" /> : <Navigation size={18} />}
              {detecting ? '定位中…' : '允许定位'}
            </button>
            <button
              type="button"
              onClick={() => setShowManual(true)}
              disabled={detecting}
              className="w-full py-3 rounded-xl font-bold bg-white/5 text-white/70 border border-white/10 hover:bg-white/10"
            >
              手动选择城市
            </button>
            <button
              type="button"
              onClick={onDeny}
              disabled={detecting}
              className="w-full py-3 rounded-xl font-bold text-white/40 hover:text-white/60"
            >
              暂不开启
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const LocationPicker = ({  value,
  onChange,
  autoDetectOnMount = false,
  showAutoConsent = false,
  compact = false,
  showToast
}) => {
  const [province, setProvince] = useState(value?.province || CHINA_PROVINCES[0]?.name || '');
  const [city, setCity] = useState(value?.city || '');
  const [detecting, setDetecting] = useState(false);
  const [autoConsent, setAutoConsent] = useState(!!value?.locationAgreed);
  const [showManual, setShowManual] = useState(!value?.city);

  const cities = useMemo(
    () => CHINA_PROVINCES.find((p) => p.name === province)?.cities || [],
    [province]
  );

  useEffect(() => {
    if (value?.province) setProvince(value.province);
    if (value?.city) {
      setCity(value.city);
      setShowManual(false);
    }
  }, [value?.city, value?.province]);

  useEffect(() => {
    setAutoConsent(!!value?.locationAgreed);
  }, [value?.locationAgreed]);

  useEffect(() => {
    if (cities.length && !cities.includes(city)) {
      setCity(cities[0] || '');
    }
  }, [province, cities, city]);

  const applyLocation = (info) => {
    const profile = buildLocationProfile(info);
    onChange?.({ ...value, ...profile });
  };

  const runAutoDetect = async (options = {}) => {
    const { keepConsent = true } = options;
    setDetecting(true);
    try {
      const detected = await detectUserLocation();
      applyLocation({
        ...detected,
        locationAgreed: keepConsent ? true : !!value?.locationAgreed
      });
      if (keepConsent) setAutoConsent(true);
      setShowManual(false);
      showToast?.(`已定位到 ${buildLocationProfile(detected).location}`);
    } catch (error) {
      console.warn('自动定位失败:', error);
      setShowManual(true);
      showToast?.(getGeolocationErrorMessage(error), 'warning');
    } finally {
      setDetecting(false);
    }
  };

  useEffect(() => {
    if (!autoDetectOnMount || !autoConsent || value?.city) return;
    runAutoDetect();
  }, [autoDetectOnMount, autoConsent, value?.city]);

  const handleManualApply = () => {
    const manual = buildManualLocation(province, city);
    if (!manual) return showToast?.('请选择所在城市');
    applyLocation({
      ...manual,
      locationAgreed: autoConsent
    });
    setShowManual(false);
    showToast?.(`已设置为 ${buildLocationProfile(manual).location}`);
  };

  const handleAutoConsentToggle = (checked) => {
    setAutoConsent(checked);
    const next = setAutoLocationConsent(value || {}, checked);
    onChange?.(next);
    if (checked) {
      runAutoDetect();
    } else {
      showToast?.('已关闭随时自动定位，当前城市仍会保留');
    }
  };

  return (
    <div className={`space-y-3 ${compact ? '' : 'p-3 bg-white/5 rounded-xl border border-white/5'}`}>
      {showAutoConsent && (
        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={autoConsent}
            onChange={(e) => handleAutoConsentToggle(e.target.checked)}
            className="mt-1 accent-amber-500"
          />
          <span className="text-[10px] text-white/50 leading-relaxed">
            允许随时自动定位（打开应用时自动更新城市；关闭后仍保留已保存城市，可随时手动重新定位）
          </span>
        </label>
      )}

      {value?.city && !showManual && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <MapPin size={16} className="text-amber-500 shrink-0" />
              <span className="text-xs font-bold text-amber-400 truncate">
                当前城市：{value.location || value.city}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowManual(true)}
              className="text-[10px] text-white/50 hover:text-amber-400 shrink-0"
            >
              修改
            </button>
          </div>
          <button
            type="button"
            onClick={() => runAutoDetect({ keepConsent: autoConsent })}
            disabled={detecting}
            className="w-full py-2 rounded-lg text-xs font-bold bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 disabled:opacity-40 flex items-center justify-center gap-1.5"
          >
            {detecting ? <Loader2 size={14} className="animate-spin" /> : <Navigation size={14} />}
            {detecting ? '定位中…' : '重新定位'}
          </button>
        </div>
      )}

      {(showManual || !value?.city) && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <select
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              className="flex-1 bg-black/30 border border-white/10 rounded-lg px-2 py-2 text-xs text-white outline-none focus:border-amber-500/50"
            >
              {CHINA_PROVINCES.map((p) => (
                <option key={p.name} value={p.name} className="bg-zinc-900">
                  {p.name}
                </option>
              ))}
            </select>
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="flex-1 bg-black/30 border border-white/10 rounded-lg px-2 py-2 text-xs text-white outline-none focus:border-amber-500/50"
            >
              {cities.map((c) => (
                <option key={c} value={c} className="bg-zinc-900">
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleManualApply}
              className="flex-1 py-2 rounded-lg text-xs font-bold bg-white/10 text-white/80 hover:bg-white/15"
            >
              确认城市
            </button>
            <button
              type="button"
              onClick={() => runAutoDetect({ keepConsent: autoConsent })}
              disabled={detecting}
              className="flex-1 py-2 rounded-lg text-xs font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30 disabled:opacity-40 flex items-center justify-center gap-1"
            >
              {detecting ? <Loader2 size={14} className="animate-spin" /> : <Navigation size={14} />}
              {detecting ? '定位中…' : '自动定位'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
